import { useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import PageHeader from "@/components/feature/PageHeader";
import SectionHeading from "@/components/feature/SectionHeading";
import { brand } from "@/mocks/siteData";

const principles = [
  {
    icon: "ri-scales-3-line",
    title: "客观整理",
    desc: "只做角色与设定的资料梳理，不夸大、不虚构，也不编造不存在的权威认证。",
  },
  {
    icon: "ri-heart-3-line",
    title: "尊重创作",
    desc: "坚持引导读者前往正版平台阅读，不提供任何盗版内容或阅读链接。",
  },
  {
    icon: "ri-search-eye-line",
    title: "持续更新",
    desc: "随着作品推进与同好补充，持续修订资料，让信息尽可能准确、完整。",
  },
];

const feedbacks = [
  {
    text: "把店主是谁、人物关系讲得明明白白，入坑前看这一页就够了。",
    author: "一位杂货铺新读者",
  },
  {
    text: "墙上的洞到底干嘛的，终于有人用大白话说清楚了。",
    author: "追更半年的老读者",
  },
  {
    text: "设定分类很清晰，看完再去读正篇，体验完全不一样。",
    author: "资料收集爱好者",
  },
];

const FORM_URL = "https://readdy.ai/api/form/da2m2g2amq5jpe1hs0d0";

function ContactForm() {
  const [status, setStatus] = useState<"idle" | "loading" | "ok" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    // 蜜罐检测：命中则给出与正常提交一致的反馈，避免暴露给机器人
    const honeypot = String(data.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("ok");
      return;
    }

    setStatus("loading");
    setFormError("");

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key !== "website_alt") {
        payload.append(key, String(value));
      }
    });

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = /spam|form data is spam/i.test(serverMsg);

      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(serverMsg && !isSpam ? serverMsg : "提交失败，请稍后再试。");
        return;
      }

      setStatus("ok");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后再试。");
    }
  };

  return (
    <form
      id="contact"
      data-readdy-form="洞洞杂货铺留言表单"
      className="contact-form rounded-lg border border-background-200/70 bg-background-100 p-6 md:p-8"
      onSubmit={handleSubmit}
    >
      <h2 className="font-heading text-xl font-bold text-foreground-950">给我们留言</h2>
      <p className="mt-1 text-sm text-foreground-600">
        有想补充的角色或设定细节？欢迎留言告诉我们。
      </p>

      <div className="mt-6 space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="name" className="mb-1.5 block text-sm font-bold text-foreground-900">
              你的称呼
            </label>
            <input
              id="name"
              name="name"
              type="text"
              required
              maxLength={30}
              placeholder="怎么称呼你"
              className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
            />
          </div>
          <div>
            <label htmlFor="email" className="mb-1.5 block text-sm font-bold text-foreground-900">
              联系邮箱
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              maxLength={80}
              placeholder="name@example.com"
              className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
            />
          </div>
        </div>
        <div>
          <label htmlFor="message" className="mb-1.5 block text-sm font-bold text-foreground-900">
            留言内容
          </label>
          <textarea
            id="message"
            name="message"
            required
            maxLength={500}
            rows={5}
            placeholder="想说的话（500 字以内）"
            className="w-full resize-y rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>

        {/* 蜜罐字段 */}
        <div className="hp-field" aria-hidden="true">
          <input
            type="text"
            name="website_alt"
            tabIndex={-1}
            autoComplete="off"
            readOnly
          />
        </div>

        <div>
          <button
            type="submit"
            disabled={status === "loading"}
            className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {status === "loading" ? "提交中…" : "提交留言"}
            <i className="ri-send-plane-line" />
          </button>
        </div>

        {status === "ok" ? (
          <p className="rounded-md bg-accent-100 px-3 py-2.5 text-sm font-bold text-accent-800">
            留言已收到，感谢你的补充！
          </p>
        ) : null}
        {status === "error" ? (
          <p className="rounded-md bg-primary-100 px-3 py-2.5 text-sm font-bold text-primary-800">
            {formError}
          </p>
        ) : null}
      </div>
    </form>
  );
}

export default function About() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "/" },
          { "@type": "ListItem", position: 2, name: "关于本站", item: "/about" },
        ],
      },
      {
        "@type": "AboutPage",
        name: "关于洞洞杂货铺角色与设定科普站",
        description:
          "本站为《洞洞杂货店》同好资料整理站，客观梳理角色、人物关系与店铺设定，并引导读者前往正版平台阅读。",
      },
      { "@type": "ContactPage", name: "联系我们", description: "通过留言表单与本站联系。" },
    ],
  };

  return (
    <>
      <Seo
        title="关于本站｜洞洞杂货铺角色与设定科普站"
        description="关于洞洞杂货铺角色与设定科普站：了解站点定位、内容原则与读者口碑，也可以通过留言表单与我们联系。"
        keywords="洞洞杂货铺,关于本站,角色介绍,设定科普"
        path="/about"
        jsonLd={jsonLd}
      />
      <PageHeader
        crumb={[{ label: "首页", to: "/" }, { label: "关于本站" }]}
        title="关于本站"
        subtitle="这是一份为《洞洞杂货店》读者整理的角色与设定资料，愿它能陪你更快走进那间乡下的杂货铺。"
      />

      <section className="w-full bg-background-50 py-12 md:py-16">
        <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-lg border border-background-200/70 bg-background-100 p-6 md:p-8">
              <SectionHeading align="left" eyebrow="站点定位" title={`关于${brand.name}科普站`} />
              <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                {brand.description}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                本站围绕「店主是谁、主要人物关系、店铺设定」三个问题展开，把散落在各话里的信息整理成清晰易懂的资料，
                帮助刚入坑的朋友快速认清漫画里的每个人，也帮助老读者系统回顾设定。
              </p>
            </div>

            <div className="rounded-lg border border-background-200/70 bg-background-100 p-6 md:p-8">
              <SectionHeading align="left" eyebrow="内容原则" title="我们如何整理资料" />
              <ul className="mt-4 space-y-4">
                {principles.map((p) => (
                  <li key={p.title} className="flex gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                      <i className={`${p.icon} text-lg`} />
                    </span>
                    <div>
                      <h3 className="text-sm font-bold text-foreground-950">{p.title}</h3>
                      <p className="mt-0.5 text-sm text-foreground-600">{p.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 py-12 md:py-16">
        <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
          <SectionHeading
            eyebrow="读者口碑"
            title="来自同好的反馈"
            desc="一句话就是最好的鼓励。"
          />
          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
            {feedbacks.map((f) => (
              <figure
                key={f.author}
                className="flex flex-col rounded-lg border border-background-200/70 bg-background-50 p-5"
              >
                <span className="flex gap-1 text-secondary-500">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill" />
                  ))}
                </span>
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                  “{f.text}”
                </blockquote>
                <figcaption className="mt-4 text-xs font-bold text-foreground-500">
                  — {f.author}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50 py-12 md:py-16">
        <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="flex flex-col justify-center">
              <SectionHeading
                align="left"
                eyebrow="联系我们"
                title="一起把资料补得更全"
                desc="发现角色或设定有出入？或者想补充更多细节？欢迎通过右侧表单留言，我们会认真对待每一条反馈。"
              />
              <div className="mt-6 space-y-3 text-sm text-foreground-700">
                <p className="flex items-center gap-2">
                  <i className="ri-mail-line text-primary-700" />
                  联系邮箱：fan@dongdong-shop.example
                </p>
                <p className="flex items-center gap-2">
                  <i className="ri-book-open-line text-primary-700" />
                  建议先阅读正版：请搜索《洞洞杂货店》正版平台
                </p>
                <p className="flex items-center gap-2">
                  <i className="ri-arrow-right-line text-primary-700" />
                  想先补课？去 <Link className="font-bold text-accent-700 hover:underline" to="/characters">角色介绍</Link> 或{" "}
                  <Link className="font-bold text-accent-700 hover:underline" to="/settings">店铺设定</Link>
                </p>
              </div>
            </div>
            <ContactForm />
          </div>
        </div>
      </section>
    </>
  );
}